import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusOutlined, SearchOutlined, ReloadOutlined, FilterOutlined, InfoCircleOutlined } from '@ant-design/icons';
import SiteCard from './components/SiteCard';
import EmptyState from '../../components/shared/EmptyState/EmptyState';
import SideModal from '../../components/shared/SideModal/SideModal';
import { servicesService } from '../../services';
import { getServiceAge } from '../../utils/serviceHelpers';

interface Site {
  id: string;
  name: string;
  status: 'active' | 'inactive' | 'deploying' | 'error';
  lastDeploy: string;
  type: string;
  url?: string;
  domain?: string;
  blueprintId?: string;
  bundleId?: string;
  staticIp?: string;
}

const Sites: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'active' | 'inactive' | 'deploying' | 'error' | 'all'>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);

  const mapServiceToSite = useCallback((service: any): Site => {
    const mapStatus = (status: string): 'active' | 'inactive' | 'deploying' | 'error' => {
      switch (status?.toLowerCase()) {
        case 'running':
          return 'active';
        case 'stopped':
          return 'inactive';
        case 'creating':
        case 'initializing':
        case 'in_progress':
          return 'deploying';
        case 'error':
        case 'error_create_stack':
        case 'failed':
          return 'error';
        default:
          return 'inactive';
      }
    };

    const metadata = service.metadata || {};
    const siteConfig = metadata.siteConfig || {};

    return {
      id: service.id,
      name: service.name || siteConfig.name || 'Unnamed Site',
      status: mapStatus(service.status),
      lastDeploy: getServiceAge(
        service.updatedAt ? new Date(service.updatedAt) : new Date()
      ),
      type: siteConfig.blueprintId || service.blueprintId || 'wordpress',
      url: service.endpointUrl || (service.staticIp ? `http://${service.staticIp}` : undefined),
      domain: service.domainName,
      blueprintId: siteConfig.blueprintId || service.blueprintId,
      bundleId: siteConfig.bundleId || service.bundleId,
      staticIp: service.staticIp || service.publicIp,
    };
  }, []);

  useEffect(() => {
    const fetchSites = async () => {
      try {
        setLoading(true);
        const response = await servicesService.getByCategory('SITE');
        const mapped = response.services.map(mapServiceToSite);
        setSites(mapped);
      } catch (error) {
        console.error('Error fetching sites:', error);
        setSites([]);
      } finally {
        setLoading(false);
      }
    };
    fetchSites();
  }, [mapServiceToSite]);

  const refreshSites = useCallback(async () => {
    try {
      setLoading(true);
      const response = await servicesService.getByCategory('SITE');
      const mapped = response.services.map(mapServiceToSite);
      setSites(mapped);
    } catch (error) {
      console.error('Error refreshing sites:', error);
    } finally {
      setLoading(false);
    }
  }, [mapServiceToSite]);

  const filteredSites = sites.filter((site) => {
    const matchesSearch = 
      site.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      site.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (site.url && site.url.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (site.domain && site.domain.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = filterStatus === 'all' || site.status === filterStatus;
    const matchesType = filterType === 'all' || site.type === filterType;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  const siteTypes = Array.from(new Set(sites.map(s => s.type)));

  return (
    <div className="max-w-[1600px] mx-auto px-8 py-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Sites</h1>
              <button
                onClick={() => setShowHelpModal(true)}
                className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                title="Get help about Sites"
              >
                <InfoCircleOutlined className="text-lg" />
              </button>
            </div>
            <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">Website Hosting</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 max-w-2xl">
              Deploy and manage websites powered by AWS Lightsail. Support for WordPress, Drupal, Ghost, Joomla, PrestaShop, and more.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="https://docs.kubiy.com/static-sites"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
            >
              <InfoCircleOutlined />
              Documentación
            </a>
            <button
              onClick={refreshSites}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ReloadOutlined />
              Refresh
            </button>
            <button
              onClick={() => navigate('/sites/create')}
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <PlusOutlined />
              New Site
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <SearchOutlined className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, type, URL or domain..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white dark:bg-gray-800 dark:text-white"
            />
          </div>

          {/* Type Filter */}
          <div className="flex items-center gap-2">
            <FilterOutlined className="text-gray-400" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white dark:bg-gray-800 dark:text-white"
            >
              <option value="all">All Types</option>
              {siteTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as typeof filterStatus)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white dark:bg-gray-800 dark:text-white"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="deploying">Deploying</option>
            <option value="error">Error</option>
          </select>

          {/* View Mode */}
          <div className="flex items-center border border-gray-300 dark:border-gray-600 rounded-md overflow-hidden">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-2 text-sm ${
                viewMode === 'grid' ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 text-sm border-l border-gray-300 dark:border-gray-600 ${
                viewMode === 'list' ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Sites Grid/List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-gray-500 dark:text-gray-400">Loading sites...</div>
        </div>
      ) : filteredSites.length === 0 ? (
        <EmptyState
          title="No sites found"
          description={searchTerm || filterStatus !== 'all' || filterType !== 'all' 
            ? "Try adjusting your search or filters to find what you're looking for."
            : "Start deploying your websites today! Create your first site and get it online in minutes."}
          action={!searchTerm && filterStatus === 'all' && filterType === 'all' ? {
            label: 'Create New Site',
            onClick: () => navigate('/sites/create')
          } : undefined}
        />
      ) : (
        <>
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredSites.map((site) => (
                <div key={site.id} onClick={() => navigate(`/sites/${site.id}`)} className="cursor-pointer">
                  <SiteCard
                    name={site.name}
                    status={site.status}
                    lastDeploy={site.lastDeploy}
                    type={site.type}
                    url={site.url}
                    domain={site.domain}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredSites.map((site) => (
                <div key={site.id} onClick={() => navigate(`/sites/${site.id}`)} className="cursor-pointer bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
                  <SiteCard
                    name={site.name}
                    status={site.status}
                    lastDeploy={site.lastDeploy}
                    type={site.type}
                    url={site.url}
                    domain={site.domain}
                  />
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Summary Stats */}
      {filteredSites.length > 0 && (
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Sites</div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{sites.length}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Active</div>
            <div className="text-2xl font-bold text-green-600">
              {sites.filter(s => s.status === 'active').length}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Deploying</div>
            <div className="text-2xl font-bold text-blue-600">
              {sites.filter(s => s.status === 'deploying').length}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Inactive</div>
            <div className="text-2xl font-bold text-gray-600 dark:text-gray-400">
              {sites.filter(s => s.status === 'inactive').length}
            </div>
          </div>
        </div>
      )}

      {/* Help Modal */}
      <SideModal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        title="About Sites"
      >
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">What are Sites?</h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              Sites allow you to deploy and manage websites powered by AWS Lightsail. 
              Choose from popular platforms like WordPress, Drupal, Ghost, and more. 
              Each site gets its own dedicated instance with a static IP address.
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Supported Platforms</h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              All active Lightsail blueprints are available, including WordPress, Drupal, Ghost, Joomla, PrestaShop, Magento, LAMP, Node.js, and more. The list is updated automatically from AWS.
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">How It Works</h3>
            <div className="space-y-3">
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-6 h-6 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                  <span className="text-xs font-bold text-blue-600 dark:text-blue-400">1</span>
                </div>
                <p className="text-gray-700 dark:text-gray-300">Choose a blueprint (WordPress, Drupal, etc.)</p>
              </div>
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-6 h-6 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                  <span className="text-xs font-bold text-blue-600 dark:text-blue-400">2</span>
                </div>
                <p className="text-gray-700 dark:text-gray-300">Select a plan (CPU, RAM, storage)</p>
              </div>
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-6 h-6 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                  <span className="text-xs font-bold text-blue-600 dark:text-blue-400">3</span>
                </div>
                <p className="text-gray-700 dark:text-gray-300">Pay and your site is automatically provisioned</p>
              </div>
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-6 h-6 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                  <span className="text-xs font-bold text-blue-600 dark:text-blue-400">4</span>
                </div>
                <p className="text-gray-700 dark:text-gray-300">Access your site via the assigned static IP</p>
              </div>
            </div>
          </div>
        </div>
      </SideModal>
    </div>
  );
};

export default Sites;